/**
 * @fileoverview Single source of truth for Deftune subscription tier metadata, descriptions, and feature lists.
 */

/**
 * Standard tier definitions and feature breakdowns for Free Mode.
 */
export const SUBSCRIPTION_TIERS = [
    {
        key: 'free',
        name: 'Free',
        desc: 'Evaluation mode for quick practice sessions. No registration required.',
        price: '$0',
        period: 'Free forever',
        buttonText: 'Try Free Now',
        buttonLink: '/app',
        buttonSecondary: true,
        features: [
            { text: 'Instant browser native audio decoding', enabled: true },
            { text: 'Full pitch & tempo manipulation', enabled: true },
            { text: 'Standard piano-roll visualization', enabled: true },
            { text: '8 band graphic equalizer', enabled: true },
            { text: 'Beat entry', enabled: false },
            { text: 'Midi integration', enabled: false }
        ],
        featured: false
    },
    {
        key: 'advanced',
        name: 'Advanced',
        badge: 'Most Popular',
        desc: 'For serious musicians, arrangers, and transcribers.',
        buttonText: 'Get Advanced',
        buttonLink: '/app#signup',
        buttonSecondary: false,
        variantIds: {
            monthly: '2054448',
            yearly: '2054452'
        },
        features: [
            { text: 'Everything in Free', enabled: true },
            { text: 'Beat entry', enabled: true },
            { text: 'Midi integration', enabled: false }
        ],
        featured: true
    },
    {
        key: 'pro',
        name: 'Professional',
        desc: 'Complete toolkit for professional performers and educators.',
        buttonText: 'Get Professional',
        buttonLink: '/app#signup',
        buttonSecondary: false,
        variantIds: {
            yearly: '2054453'
        },
        features: [
            { text: 'Everything in Advanced', enabled: true },
            { text: 'Midi integration', enabled: true },
            { text: 'High-priority support', enabled: true }
        ],
        featured: false
    },
    {
        key: 'unlimited',
        name: 'Unlimited',
        desc: 'For studios, bands, and music schools up to 20 users.',
        buttonText: 'Get Unlimited',
        buttonLink: '/app#signup',
        buttonSecondary: true,
        variantIds: {
            yearly: '2054460'
        },
        features: [
            { text: 'Everything in Professional', enabled: true },
            { text: 'Multi-user access (up to 20 seats)', enabled: true },
            { text: 'Immediate access to all new features', enabled: true },
            { text: 'Early access to experimental features', enabled: true },
            { text: 'Dedicated SLA & support guarantees', enabled: true }
        ],
        featured: false
    }
];

/**
 * Direct lookup map from Lemon Squeezy Variant ID to Tier key and frequency.
 */
export const VARIANT_ID_TO_SUBSCRIPTION = {
    '2054448': { tier: 'advanced', billing: 'monthly' },
    '2054452': { tier: 'advanced', billing: 'yearly' },
    '2054453': { tier: 'pro', billing: 'yearly' },
    '2054460': { tier: 'unlimited', billing: 'yearly' }
};

/**
 * Computes price and period string given a tier's pricing rates and the active billing frequency.
 * @param {Object|null} tierPricing - Object containing monthly and yearly price numbers or null.
 * @param {boolean} isYearly - Whether yearly billing is currently selected.
 * @returns {{ price: string, period: string }} Formatted price and billing period string.
 */
export function formatTierPricing(tierPricing, isYearly) {
    if (!tierPricing) return { price: '$0', period: '' };
    if (isYearly) {
        if (tierPricing.yearly !== null && tierPricing.yearly !== undefined) {
            return { price: `$${tierPricing.yearly}`, period: 'per year (billed annually)' };
        } else if (tierPricing.monthly !== null && tierPricing.monthly !== undefined) {
            return { price: `$${tierPricing.monthly}`, period: 'per month (monthly only)' };
        }
    } else {
        if (tierPricing.monthly !== null && tierPricing.monthly !== undefined) {
            return { price: `$${tierPricing.monthly}`, period: 'per month (billed monthly)' };
        } else if (tierPricing.yearly !== null && tierPricing.yearly !== undefined) {
            return { price: `$${tierPricing.yearly}`, period: 'per year (annual only)' };
        }
    }
    return { price: '$0', period: '' };
}
